import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';

// ── Delivery schedule editor (Phase 1 slice A) ──
//
// Compact editor rendered inside the expanded customer card on the Customers
// tab. The customer banner and checkout defaults used to be guessed by
// substring-scanning the SOS notes; this edits the portal-owned structured
// schedule that now wins. Rows seeded by the backfill carry a "from SOS notes
// — please confirm" badge until a human saves them.

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function DeliverySettingsEditor({ customer, readOnly }) {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);
  const [method, setMethod] = useState('route');
  const [cutoffTime, setCutoffTime] = useState('12:00 PM');
  const [notes, setNotes] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [hasRow, setHasRow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.getDeliverySettings(customer.id);
        if (cancelled) return;
        if (r.settings) {
          setDays(r.settings.deliveryDays || []);
          setMethod(r.settings.shippingMethod || 'route');
          setCutoffTime(r.settings.cutoffTime || '12:00 PM');
          setNotes(r.settings.deliveryNotes || '');
          setSeeded(!!r.settings.seededFromNotes);
          setHasRow(true);
        }
        setSuggested(r.suggested || null);
      } catch (e) {
        setStatus(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customer.id]);

  function toggleDay(day) {
    setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  }

  async function save() {
    setSaving(true);
    setStatus('');
    try {
      await adminApi.setDeliverySettings(customer.id, {
        deliveryDays: days, shippingMethod: method, cutoffTime, deliveryNotes: notes,
      });
      setSeeded(false);
      setHasRow(true);
      setStatus('Saved ✓');
    } catch (e) {
      setStatus(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={st.wrap}><span style={st.muted}>Loading delivery schedule…</span></div>;

  return (
    <div style={st.wrap}>
      <div style={st.header}>
        <b style={{ fontSize: '0.85rem' }}>Delivery schedule</b>
        {seeded && <span style={st.seededBadge}>auto-filled from SOS notes — please confirm</span>}
        {!hasRow && suggested && (
          <button
            style={st.suggestBtn}
            onClick={() => { setDays(suggested.deliveryDays || []); setMethod(suggested.shippingMethod || 'route'); }}
          >
            Use suggestion: {(suggested.deliveryDays || []).join(', ') || 'no day'}{suggested.shippingMethod === 'fedex' ? ' · FedEx' : ''}
          </button>
        )}
      </div>
      <div style={st.daysRow}>
        {WEEKDAYS.map(day => (
          <label key={day} style={{ ...st.dayChip, ...(days.includes(day) ? st.dayChipOn : {}) }}>
            <input type="checkbox" style={{ display: 'none' }} disabled={readOnly}
              checked={days.includes(day)} onChange={() => toggleDay(day)} />
            {day.slice(0, 3)}
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={st.input} value={method} disabled={readOnly} onChange={e => setMethod(e.target.value)}>
          <option value="route">Route delivery</option>
          <option value="fedex">FedEx</option>
        </select>
        <input style={{ ...st.input, width: 110 }} value={cutoffTime} disabled={readOnly}
          onChange={e => setCutoffTime(e.target.value)} placeholder="Cutoff" title="Order cutoff time shown to the customer" />
        <input style={{ ...st.input, flex: 1, minWidth: 160 }} value={notes} disabled={readOnly}
          onChange={e => setNotes(e.target.value)} placeholder="Delivery notes shown to the customer (optional)" />
        {!readOnly && (
          <button style={{ ...st.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
        {status && <span style={{ fontSize: '0.8rem', color: status.includes('✓') ? '#2e7d32' : '#c0392b' }}>{status}</span>}
      </div>
    </div>
  );
}

const st = {
  wrap: { background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: '0.6rem 0.75rem', margin: '0.5rem 0' },
  header: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.45rem', flexWrap: 'wrap' },
  muted: { color: '#888', fontSize: '0.85rem' },
  seededBadge: { fontSize: '0.72rem', background: '#fff3e0', color: '#e65100', padding: '0.15rem 0.5rem', borderRadius: 10 },
  suggestBtn: { fontSize: '0.75rem', background: 'none', border: '1px dashed #A50B18', color: '#A50B18', borderRadius: 10, padding: '0.15rem 0.5rem', cursor: 'pointer' },
  daysRow: { display: 'flex', gap: '0.35rem', marginBottom: '0.5rem', flexWrap: 'wrap' },
  dayChip: { fontSize: '0.78rem', padding: '0.25rem 0.55rem', border: '1px solid #ddd', borderRadius: 12, cursor: 'pointer', background: '#fff', color: '#555', userSelect: 'none' },
  dayChipOn: { background: '#A50B18', border: '1px solid #A50B18', color: '#fff', fontWeight: 600 },
  input: { padding: '0.35rem 0.5rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.82rem', background: '#fff' },
  saveBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
};
